// ═══════════════════════════════════════════════════════════════════════════════
// Employer dashboard — post-pairing getting-started checklist card
// ═══════════════════════════════════════════════════════════════════════════════

import type { KeyboardEvent } from 'react';
import type { EmployerGettingStartedProgress } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../ui/AppButton';

type GettingStartedStepId = 'task' | 'budget' | 'family' | 'pin';

type GettingStartedStep = {
  id: GettingStartedStepId;
  done: boolean;
  titleKey: string;
  hintKey: string;
  ctaKey: string;
  ariaKey: string;
};

type EmployerGettingStartedCardProps = {
  progress: EmployerGettingStartedProgress;
  busy?: boolean;
  /** inline = dashboard card with dismiss; compact = HelpPopover content */
  variant?: 'inline' | 'compact';
  onDismiss?: () => void;
  onStepClick: (stepId: GettingStartedStepId) => void;
};

export type { GettingStartedStepId };

export const EmployerGettingStartedCard = ({
  progress,
  busy = false,
  variant = 'inline',
  onDismiss,
  onStepClick,
}: EmployerGettingStartedCardProps) => {
  const { t } = useLanguage();
  const isCompact = variant === 'compact';

  const steps: GettingStartedStep[] = [
    {
      id: 'task',
      done: progress.hasTask,
      titleKey: 'employerDashboard.gettingStarted.stepTaskTitle',
      hintKey: 'employerDashboard.gettingStarted.stepTaskHint',
      ctaKey: 'employerDashboard.gettingStarted.stepCta',
      ariaKey: 'employerDashboard.gettingStarted.stepTaskAria',
    },
    {
      id: 'budget',
      done: progress.hasBudget,
      titleKey: 'employerDashboard.gettingStarted.stepBudgetTitle',
      hintKey: 'employerDashboard.gettingStarted.stepBudgetHint',
      ctaKey: 'employerDashboard.gettingStarted.stepCta',
      ariaKey: 'employerDashboard.gettingStarted.stepBudgetAria',
    },
    {
      id: 'family',
      done: progress.hasFamilyMember,
      titleKey: 'employerDashboard.gettingStarted.stepFamilyTitle',
      hintKey: 'employerDashboard.gettingStarted.stepFamilyHint',
      ctaKey: 'employerDashboard.gettingStarted.stepCta',
      ariaKey: 'employerDashboard.gettingStarted.stepFamilyAria',
    },
    {
      id: 'pin',
      done: progress.hasPinMessage,
      titleKey: 'employerDashboard.gettingStarted.stepPinTitle',
      hintKey: 'employerDashboard.gettingStarted.stepPinHint',
      ctaKey: 'employerDashboard.gettingStarted.stepCta',
      ariaKey: 'employerDashboard.gettingStarted.stepPinAria',
    },
  ];

  const completedCount = steps.filter((step) => step.done).length;
  const totalCount = steps.length;

  const handleDismissKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!busy && onDismiss) {
        onDismiss();
      }
    }
  };

  const handleStepKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    stepId: GettingStartedStepId,
    done: boolean,
  ): void => {
    if (done) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onStepClick(stepId);
    }
  };

  const body = (
    <>
      <div className={`mb-3 flex items-start justify-between gap-3 ${isCompact ? 'mb-2' : ''}`}>
        <div className="min-w-0">
          {!isCompact ? (
            <h2
              id="dash-getting-started-heading"
              className="text-base font-semibold text-gray-900 dark:text-gray-100"
            >
              {t('employerDashboard.gettingStarted.title')}
            </h2>
          ) : (
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
              {t('employerDashboard.gettingStarted.title')}
            </p>
          )}
          <p
            className={`mt-1 text-gray-600 dark:text-gray-300 ${
              isCompact ? 'text-xs leading-relaxed' : 'text-sm'
            }`}
          >
            {t('employerDashboard.gettingStarted.subtitle')}
          </p>
          <p
            className={`mt-1 font-medium text-primary-700 dark:text-primary-300 ${
              isCompact ? 'text-[11px]' : 'text-xs'
            }`}
          >
            {t('employerDashboard.gettingStarted.progress', {
              done: completedCount,
              total: totalCount,
            })}
          </p>
        </div>
        {!isCompact && onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            onKeyDown={handleDismissKeyDown}
            disabled={busy}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white/80 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            aria-label={t('employerDashboard.gettingStarted.dismissAria')}
            tabIndex={0}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : null}
      </div>

      <ul className="flex flex-col gap-2" role="list">
        {steps.map((step) => (
          <li key={step.id}>
            <div
              className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${
                step.done
                  ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-800/50 dark:bg-emerald-950/30'
                  : 'border-gray-200 bg-white/80 dark:border-gray-700 dark:bg-gray-900/60'
              }`}
            >
              <span
                className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  step.done
                    ? 'bg-emerald-500 text-white'
                    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-200'
                }`}
                aria-hidden
              >
                {step.done ? (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t(step.titleKey)}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{t(step.hintKey)}</p>
              </div>
              {step.done ? (
                <span className="shrink-0 self-center text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                  {t('employerDashboard.gettingStarted.done')}
                </span>
              ) : (
                <AppButton
                  type="button"
                  variant="primary"
                  onClick={() => onStepClick(step.id)}
                  onKeyDown={(event) => handleStepKeyDown(event, step.id, step.done)}
                  className="min-h-9 shrink-0 self-center px-3 py-1.5 text-xs"
                  aria-label={t(step.ariaKey)}
                  tabIndex={0}
                >
                  {t(step.ctaKey)}
                </AppButton>
              )}
            </div>
          </li>
        ))}
      </ul>

      {!isCompact && onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          onKeyDown={handleDismissKeyDown}
          disabled={busy}
          className="mt-3 w-full rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-white/70 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          aria-label={t('employerDashboard.gettingStarted.laterAria')}
          tabIndex={0}
        >
          {t('employerDashboard.gettingStarted.later')}
        </button>
      ) : null}
    </>
  );

  if (isCompact) {
    return <div className="max-h-[min(70vh,32rem)] overflow-y-auto">{body}</div>;
  }

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-primary-200 bg-gradient-to-br from-primary-50/90 via-white to-sky-50/80 p-4 shadow-sm dark:border-primary-800/60 dark:from-gray-900 dark:via-primary-950/40 dark:to-gray-950"
      aria-labelledby="dash-getting-started-heading"
      role="region"
      aria-label={t('employerDashboard.gettingStarted.regionAria')}
    >
      {body}
    </section>
  );
};
